import React from 'react'
import { motion } from 'framer-motion'
import { Award, Users, Target, Zap } from 'lucide-react'

const About: React.FC = () => {
  const features = [
    {
      icon: <Target className="w-6 h-6" />,
      title: "Strategy & Execution",
      description: "Bridge strategy with execution through hands-on technology leadership"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Cross-Functional Leadership",
      description: "Build and lead cross-functional teams for efficient delivery processes"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Technical Depth",
      description: "Stay hands-on with code, APIs, databases, and emerging technologies"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "AI & Innovation",
      description: "Explore AI/ML, Blockchain, Computer Vision, and 3D/AR/VR experiences"
    }
  ]

  return (
    <section id="about" className="section-padding bg-gradient-to-br from-tech-50 via-cyber-50/30 to-neon-50/30 dark:from-tech-900 dark:via-cyber-900/30 dark:to-neon-900/30">
      {/* Background Tech Pattern */}
      <div className="absolute inset-0 tech-pattern-bg opacity-5"></div>
      
      <div className="container-custom relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            About <span className="gradient-text">Me</span>
          </h2>
          <p className="text-xl text-tech-600 dark:text-tech-300 max-w-3xl mx-auto">
            An experienced Software Project Manager, Operations Head, and Technologist with a passion for bridging strategy with execution
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 items-start">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="lg:col-span-2 space-y-8"
          >
            <div className="space-y-6">
              <h3 className="text-3xl font-semibold text-tech-800 dark:text-tech-200">
                Software Project Manager & Operations Head
              </h3>
              
              <div className="space-y-4">
                <p className="text-lg text-tech-600 dark:text-tech-300 leading-relaxed">
                  I'm an experienced technologist with a strong track record in mobile apps, web platforms, and custom software delivery. 
                  Over the years, I've worked extensively across Swift, React Native, Flutter, Kotlin, Node.js, Laravel, PHP, and MySQL, 
                  while also keeping pace with emerging domains like AI/ML, Blockchain, Computer Vision, and 3D/AR/VR experiences.
                </p>
                
                <p className="text-lg text-tech-600 dark:text-tech-300 leading-relaxed">
                  My strength lies in bridging strategy with execution — I thrive at leading teams and setting up efficient delivery processes 
                  while also staying hands-on with code, APIs, databases, and integrations. Whether it's building cross-functional teams, 
                  optimizing MySQL procedures, architecting APIs, experimenting with AI-powered avatars, or integrating payment solutions, 
                  I bring a unique mix of Agile leadership and technical depth.
                </p>
                
                <p className="text-lg text-tech-600 dark:text-tech-300 leading-relaxed">
                  I also enjoy working at the intersection of AI and business applications. Recent work includes experimenting with LLMs for 
                  knowledge agents, OCR + LLM for ID verification, ElevenLabs TTS + Ready Player Me avatars, and ML models trained on 
                  MySQL datasets to deliver insights.
                </p>
              </div>
            </div>

            {/* Key Features Grid */}
            <div className="grid sm:grid-cols-2 gap-6 pt-4">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-start space-x-4 p-6 rounded-xl bg-gradient-to-br from-cyber-50/50 to-neon-50/50 dark:from-cyber-900/20 dark:to-neon-900/20 hover:from-cyber-100 dark:hover:from-cyber-800/30 transition-all duration-300 border border-cyber-200/30 dark:border-cyber-700/30 hover:shadow-lg hover:-translate-y-1 hover:shadow-cyber-500/10"
                >
                  <div className="text-cyber-500 dark:text-cyber-400 mt-1 p-2 rounded-lg bg-cyber-100 dark:bg-cyber-900/30">
                    {feature.icon}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-tech-800 dark:text-tech-200 mb-2 text-lg">
                      {feature.title}
                    </h4>
                    <p className="text-tech-600 dark:text-tech-300 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Column - Avatar/Illustration */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="lg:col-span-1 flex justify-center lg:justify-start"
          >
            <div className="relative">
              {/* Main Avatar Container */}
              <div className="relative w-72 h-72 rounded-2xl gradient-border overflow-hidden shadow-2xl glow-effect">
                <img 
                  src="/rishi-profile.jpg" 
                  alt="Rishi Malhotra - Software Project Manager & Operations Head"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Floating Elements */}
              <motion.div
                className="absolute -top-4 -right-4 w-8 h-8 bg-neon-500 rounded-full opacity-80 shadow-lg neon-glow"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                className="absolute -bottom-4 -left-4 w-6 h-6 bg-cyber-500 rounded-full opacity-80 shadow-lg glow-effect"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                className="absolute top-1/2 -right-8 w-4 h-4 bg-neon-400 rounded-full opacity-60 shadow-lg neon-glow"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default About
